import { useQuery } from '@tanstack/react-query'
import { Icone } from '@/components/ui/Icone'
import { ListeSkeleton } from '@/components/ui/Skeleton'
import { portesDunTag } from '@/lib/api/parcours'

/**
 * Les deux portes d'un tag — RG-2.38, [DECISIONS §37].
 *
 * **On demande au lieu de deviner.** Taper le tag d'une carte part toujours de
 * la même impulsion — « cette question m'a surpris » — et se termine à
 * l'opposé : l'un veut s'entraîner tout de suite, l'autre veut comprendre
 * d'abord. Choisir à la place du candidat a été essayé dans les deux sens, le
 * quiz puis la leçon ; dans les deux cas la moitié des tapes tombaient au
 * mauvais endroit, et le retour en arrière coûtait le fil de la lecture.
 *
 * **Le thème du quiz n'est pas le tag de la carte** mais le premier ancêtre qui
 * se ramifie (RG-2.25). L'écran ne le calcule pas : c'est une remontée dans
 * l'arbre, et la faire ici demanderait de charger la taxonomie entière pour un
 * menu de deux lignes.
 */
export function PortesDuTag({
  tag,
  onQuiz,
  onLecon,
  onFerme,
}: {
  tag: { path: string; label: string }
  onQuiz: (theme: { path: string; label: string }) => void
  onLecon: () => void
  onFerme: () => void
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['portes-du-tag', tag.path],
    queryFn: () => portesDunTag(tag.path),
    // L'arbre des tags ne bouge qu'à une publication éditoriale : re-demander
    // à chaque ouverture ferait payer un aller-retour pour une réponse qui
    // sera la même toute la séance.
    staleTime: 10 * 60_000,
  })

  const portes = data?.data
  const chemin = tag.path.split('.').join(' › ')

  return (
    <div
      /* **`z-40`, et pas `z-30`** — [DECISIONS §293].
         La barre du bas est elle-même en `z-30`. À égalité, c'est l'ordre du
         DOM qui tranche : elle passait donc PAR-DESSUS le bas de cette feuille,
         et masquait la porte du cours ainsi que la phrase qui la suit. Le voile
         couvrait tout l'écran, sauf la barre — qui restait visible et cliquable
         devant une modale. `PreparationDuConcours` avait déjà tiré la leçon ;
         quatre autres feuilles portaient le même défaut et remontent avec. */
      className="fixed inset-0 z-40 flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.45)' }}
      onClick={onFerme}
      role="presentation"
    >
      <div
        role="dialog"
        aria-label={`Que faire sur ${tag.label}`}
        // **`overflow-y-auto` et `overscroll-contain`** : la feuille est alignée
        // en bas, donc un contenu trop grand déborde vers le HAUT, hors du
        // cadre, sans que rien ne défile. Ce sont alors les PREMIÈRES lignes
        // qui disparaissent — le titre du thème, et la porte du quiz.
        className="flex w-full max-w-lg flex-col gap-3 overflow-y-auto overscroll-contain rounded-t-3xl p-4"
        style={{
          background: 'var(--fond)',

          // **La feuille s'arrête AU-DESSUS de la barre et du bouton rond** —
          // [DECISIONS §298]. Signalé depuis un vrai téléphone : la porte du
          // quiz passait derrière le bouton « + », et la dernière ligne sous la
          // barre. Monter la couche à `z-40` n'y suffisait pas, et la raison
          // est plus subtile qu'un empilement.
          //
          // `fixed inset-0` se résout contre le viewport de MISE EN PAGE, qui
          // reste à sa taille pleine pendant que la barre d'adresse du
          // navigateur mange le bas de l'écran. Le bas de la feuille tombe donc
          // HORS DE VUE, tandis que la barre, elle, se colle au viewport
          // réellement visible. Les 85 px sont la même mesure que la feuille du
          // bouton « + », qui avait déjà rencontré ce défaut.
          paddingBottom: 'calc(85px + env(safe-area-inset-bottom))',

          // **`dvh` et non `%`.** Un pourcentage se résoudrait contre ce même
          // viewport de mise en page, et la feuille se croirait plus de place
          // qu'elle n'en a. `dvh` suit le viewport où le doigt tape.
          maxHeight: '100dvh',
        }}
        onClick={(e) => {
          e.stopPropagation()
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-[17px] font-bold">{tag.label}</h2>
            <p className="truncate text-[13px]" style={{ color: 'var(--texte-2)' }}>
              {chemin}
            </p>
          </div>

          <button type="button" onClick={onFerme} aria-label="Fermer" className="shrink-0">
            <Icone nom="croix" taille={18} />
          </button>
        </div>

        {isLoading && <ListeSkeleton annonce="Chargement…" lignes={2} hauteur="4rem" />}

        {portes !== undefined && (
          <>
            {portes.quiz !== null && (
              <Porte
                icone="cible"
                titre="S’entraîner sur ce thème"
                /* Le thème du quiz est presque toujours PLUS LARGE que le tag
                   de la carte : le dire évite que le candidat croie à un
                   quiz sur cette seule question. */
                detail={`Quiz chronométré — ${portes.quiz.label}`}
                onClick={() => {
                  onQuiz(portes.quiz as { path: string; label: string })
                }}
              />
            )}

            {/* **La porte du cours ne s'affiche que si elle mène quelque
                part** — §179. Le serveur ne rend plus que des leçons ouvrables ;
                un thème de branche n'en désigne aucune, et n'a donc que le
                quiz — qui est justement d'autant meilleur que le thème est
                large. */}
            {portes.cours !== null && (
              <Porte
                icone="lecon"
                titre="Lire la partie du cours"
                detail={portes.cours.chapitre_titre}
                onClick={onLecon}
              />
            )}

            {portes.quiz === null && portes.cours === null && (
              // RG-D16 — un écran vide dit ce qui manque. « Rien à afficher »
              // laisserait croire à une panne.
              <p className="text-[15px]" style={{ color: 'var(--texte-2)' }}>
                Ce thème n’a pas encore de cours ni assez de questions pour un
                quiz. Il en aura : le corpus se remplit chapitre par chapitre.
              </p>
            )}
          </>
        )}

        {/* RG-3.16 — dit une fois, ici, plutôt qu'en travers de la lecture.
            Le candidat qui ouvre le cours depuis une carte doit savoir que
            cela ne coche rien : sans cette phrase, il croirait avoir avancé
            dans son Parcours. */}
        {portes?.cours != null && (
          <p className="text-[13px]" style={{ color: 'var(--texte-2)' }}>
            Lire d’ici ne compte pas comme une section terminée.
          </p>
        )}
      </div>
    </div>
  )
}

function Porte({
  icone,
  titre,
  detail,
  onClick,
}: {
  icone: 'cible' | 'lecon'
  titre: string
  detail: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-3 rounded-2xl border p-3 text-left"
      style={{ borderColor: 'var(--bordure)' }}
    >
      <span
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
        style={{ background: 'var(--surface-2)' }}
      >
        <Icone nom={icone} taille={20} />
      </span>

      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-bold">{titre}</span>
        <span className="block truncate text-[13px]" style={{ color: 'var(--texte-2)' }}>
          {detail}
        </span>
      </span>

      <Icone nom="fleche-droite" taille={16} />
    </button>
  )
}
